import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Close } from '@ankr.com/ui';
import { PopoverProps } from '@mui/material';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { AccountRoutesConfig } from 'modules/account';
import { ReactComponent as WarningIcon } from 'modules/common/assets/warning.svg';
import { useTranslation } from 'modules/i18n';

import translation from './lowBalancePopup.translation';

interface Props extends PopoverProps {
  className?: string;
  onClose: () => void;
}

export function LowBalancePopup({
  className,
  onClose,
  ...rest
}: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent): void => {
    event.stopPropagation();
    onClose();
    navigate(AccountRoutesConfig.account.generatePath());
  };

  const handleClose = (event: MouseEvent): void => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Popover
      data-tid="layout-header-low-balance-popup"
      classes={{ paper: cx(classes.root, className) }}
      onClose={handleClose}
      {...rest}
    >
      <div className={classes.close} onClick={handleClose}>
        <Close />
      </div>

      <div className={classes.title}>
        <WarningIcon />
        <Typography variant="button2">{t(keys.lowBalance)}</Typography>
      </div>

      <Typography variant="body2" color={theme => theme.palette.grey[900]}>
        {t(keys.topUpYourAccount)}
      </Typography>

      <Button
        data-tid="layout-header-low-balance-popup-button"
        className={classes.button}
        onClick={handleClick}
      >
        {t(keys.topUp)}
      </Button>
    </Popover>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    '&&': {
      padding: theme.spacing(4),
      minWidth: 250,
      display: 'flex',
      flexFlow: 'column nowrap',
      gap: theme.spacing(3),
      boxShadow:
        '0px 0px 1px #B8C2D3, 0px 9px 12px -6px rgba(154, 161, 176, 0.31)',
    },
  },
  title: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  close: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: theme.palette.grey[500],
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  button: {
    '&&': {
      width: 87,
    },
  },
}));
